import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuth";
import { getUsageSummary, type UsageSummary } from "@/server/usage";
import {
  type Capability,
  getLimit,
  isUnlimited,
  isOverLimit,
  nextTierWithMore,
  formatLimit,
  CAPABILITY_LABELS,
  TIER_LABELS,
} from "@/lib/quotas";
import type { Tier } from "@/hooks/useSubscription";

const QK = ["usage-summary"] as const;

const EMPTY_SUMMARY: UsageSummary = {
  tier: "free",
  periodStart: new Date().toISOString().slice(0, 10),
  counts: {} as UsageSummary["counts"],
};

export function useUsageSummary() {
  const { user, loading } = useAuth();
  return useQuery<UsageSummary>({
    queryKey: QK,
    queryFn: async () => {
      try {
        return await getUsageSummary();
      } catch (err) {
        // Auth middleware throws a raw Response on missing/invalid token
        // during the brief initial-render window. Fall back to an empty
        // summary instead of surfacing an unhandled rejection.
        if (err instanceof Response) return EMPTY_SUMMARY;
        console.error("getUsageSummary failed", err);
        return EMPTY_SUMMARY;
      }
    },
    enabled: !!user && !loading,
    staleTime: 30_000,
    retry: false,
  });
}

export type QuotaState = {
  loading: boolean;
  tier: Tier;
  used: number;
  limit: number | null;
  unlimited: boolean;
  remaining: number | null; // null = unlimited
  exceeded: boolean;
  percent: number; // 0-100, capped
  upgradeTier: Tier | null;
  label: string;
  formattedLimit: string;
  tierLabel: string;
};

export function useQuota(capability: Capability): QuotaState {
  const { data, isLoading } = useUsageSummary();
  const tier: Tier = data?.tier ?? "free";
  const used = data?.counts?.[capability] ?? 0;
  const limit = getLimit(capability, tier);
  const unlimited = isUnlimited(limit);
  const exceeded = isOverLimit(limit, used);
  const remaining = unlimited ? null : Math.max(0, (limit ?? 0) - used);
  const percent = unlimited
    ? 0
    : limit && limit > 0
      ? Math.min(100, Math.round((used / limit) * 100))
      : 100;

  return {
    loading: isLoading,
    tier,
    used,
    limit,
    unlimited,
    remaining,
    exceeded,
    percent,
    upgradeTier: nextTierWithMore(capability, tier),
    label: CAPABILITY_LABELS[capability],
    formattedLimit: formatLimit(limit),
    tierLabel: TIER_LABELS[tier],
  };
}
